<template>
  <SuperAdminShell>
    <div class="super-admin-page sa-list-page">
      <ListPageHeader
        title="Lista de Empresas"
        subtitle="Lista de todas as empresas cadastradas"
      />

      <div class="super-admin-page__shell sa-list-page__shell">
        <div class="sa-list-page__container">
          <div v-if="!hasReady" class="sa-list-page__state">
            <p>Carregando empresas...</p>
          </div>

          <template v-else>
            <section class="sa-sync" aria-labelledby="sa-sync-title">
              <div class="sa-sync__lead">
                <h2 id="sa-sync-title" class="sa-sync__title">Contas de super admin</h2>
                <p class="sa-sync__text">
                  Contas novas, edições e senhas já passam para todas as empresas.
                  Use a sincronização para vincular os super admins que ainda existem em uma só empresa.
                  A ação pode ser repetida e não duplica quem já está vinculado.
                </p>
              </div>
              <div class="sa-sync__actions">
                <button
                  v-if="!syncConfirming"
                  type="button"
                  class="sa-sync__button"
                  :disabled="syncing"
                  @click="syncConfirming = true"
                >
                  Sincronizar contas
                </button>
                <template v-else>
                  <p class="sa-sync__confirm">Vincular os super admins ativos às empresas em que o e-mail ainda não existe?</p>
                  <button
                    type="button"
                    class="sa-sync__button"
                    :disabled="syncing"
                    @click="syncCompanies"
                  >
                    {{ syncing ? 'Sincronizando...' : 'Confirmar' }}
                  </button>
                  <button
                    type="button"
                    class="sa-sync__cancel"
                    :disabled="syncing"
                    @click="syncConfirming = false"
                  >
                    Cancelar
                  </button>
                </template>
              </div>
              <p v-if="syncError" class="sa-sync__error">{{ syncError }}</p>
              <div v-if="syncResult" class="sa-sync__result">
                <p>{{ syncSummary }}</p>
                <ul v-if="syncConflicts.length">
                  <li v-for="(detail, index) in syncConflicts" :key="`${detail}-${index}`">{{ detail }}</li>
                </ul>
              </div>
            </section>

            <ReportsSummary :metrics="summaryMetrics" aria-label="Resumo das empresas" />

            <div class="sa-list-filters">
              <div class="sa-list-filters__field sa-list-filters__field--grow">
                <label for="company-filter-name">Nome</label>
                <input
                  id="company-filter-name"
                  type="text"
                  v-model="searchTerm"
                  class="sa-list-filters__control"
                  placeholder="Digite o nome da empresa..."
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__field">
                <label for="company-filter-cnpj">CNPJ</label>
                <input
                  id="company-filter-cnpj"
                  type="text"
                  v-model="searchCnpj"
                  class="sa-list-filters__control"
                  placeholder="Digite o CNPJ..."
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__field">
                <label for="company-filter-system">Sistema</label>
                <select
                  id="company-filter-system"
                  v-model="selectedType"
                  class="sa-list-filters__control"
                  @change="filterByType"
                >
                  <option value="">Todos</option>
                  <option value="1">Weeduc</option>
                  <option value="2">Weflix</option>
                </select>
              </div>
              <div v-if="selectedType === '1'" class="sa-list-filters__field">
                <label for="company-filter-type">Tipo</label>
                <select
                  id="company-filter-type"
                  v-model="selectedTypeBusiness"
                  class="sa-list-filters__control"
                  @change="onSearch"
                >
                  <option value="">Todos</option>
                  <option value="1">Escolar</option>
                  <option value="2">Corporativo</option>
                </select>
              </div>
              <div class="sa-list-filters__field">
                <label for="company-filter-date">Data de Criação</label>
                <input
                  id="company-filter-date"
                  type="date"
                  v-model="searchDate"
                  class="sa-list-filters__control"
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__actions">
                <button
                  type="button"
                  class="sa-list-filters__clear"
                  :disabled="!hasActiveFilters"
                  @click="clearFilters"
                >
                  Limpar filtros
                </button>
              </div>
            </div>

            <div class="sa-list-page__results">
              <p class="sa-list-page__results-count">{{ resultsLabel }}</p>
            </div>

            <div v-if="!filteredCompanys.length" class="sa-list-page__empty">
              <h3>Nenhuma empresa encontrada</h3>
              <p>Altere os filtros para encontrar outros resultados.</p>
              <button type="button" class="btnSave" @click="clearFilters">Limpar filtros</button>
            </div>

            <template v-else>
              <div class="sa-rep-table-wrap">
                <table class="sa-rep-table">
                  <thead>
                    <tr>
                      <th scope="col" class="is-id">ID</th>
                      <th scope="col" class="is-company">Nome</th>
                      <th scope="col">CNPJ</th>
                      <th scope="col">Sistema</th>
                      <th scope="col">Tipo</th>
                      <th scope="col">Telefone</th>
                      <th scope="col" class="is-date">Data de Criação</th>
                      <th scope="col">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in paginatedCompanies" :key="company.id">
                      <td class="is-id is-muted">{{ company.id }}</td>
                      <td class="is-company">
                        <span class="sa-rep-table__name">{{ company.name }}</span>
                      </td>
                      <td class="is-muted">{{ formatCnpj(company.idFiscal) }}</td>
                      <td><SystemBadge :type-system="company.typeSystem" /></td>
                      <td><CompanyTypeBadge :row="company" /></td>
                      <td class="is-muted">{{ formatPhone(company.phone) }}</td>
                      <td class="is-date">{{ formatDateBR(company.dateInsert) }}</td>
                      <td>
                        <router-link
                          :to="`/editar-empresa/${company.id}`"
                          class="sa-list-action-link"
                          title="Ver detalhes"
                        >
                          <fa icon="fa-solid fa-eye" />
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <ReportsPagination
                :page="currentPage"
                :total-pages="totalPages"
                :page-size="pageSize"
                @update:page="currentPage = $event"
                @update:pageSize="onPageSizeChange"
              />
            </template>
          </template>
        </div>
      </div>
    </div>
    <Loader :hasReady="hasReady" />
  </SuperAdminShell>
</template>

<script>
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue';
import { mapState } from 'vuex';
import superAdmin from '@/utils/superAdmin';
import Loader from '@/components/Weflix/Loader.vue';
import ListPageHeader from '@/components/Weflix/SuperAdmin/ListPageHeader.vue';
import ReportsSummary from '@/components/Weflix/SuperAdmin/Reports/ReportsSummary.vue';
import ReportsPagination from '@/components/Weflix/SuperAdmin/Reports/ReportsPagination.vue';
import SystemBadge from '@/components/Weflix/SuperAdmin/Reports/SystemBadge.vue';
import CompanyTypeBadge from '@/components/Weflix/SuperAdmin/Reports/CompanyTypeBadge.vue';
import { formatDateBR, formatNumberBR } from '@/utils/superAdmin/formatters';

export default {
  components: {
    SuperAdminShell,
    Loader,
    ListPageHeader,
    ReportsSummary,
    ReportsPagination,
    SystemBadge,
    CompanyTypeBadge,
  },
  data() {
    return {
      listCompanys: [],
      selectedType: '',
      selectedTypeBusiness: '',
      searchTerm: '',
      searchCnpj: '',
      searchDate: '',
      hasReady: false,
      currentPage: 1,
      pageSize: 10,
      syncing: false,
      syncConfirming: false,
      syncError: '',
      syncResult: null,
    };
  },
  computed: {
    ...mapState(['token']),
    hasActiveFilters() {
      return Boolean(
        this.searchTerm
        || this.searchCnpj
        || this.selectedType
        || this.selectedTypeBusiness
        || this.searchDate,
      );
    },
    filteredCompanys() {
      let filtered = this.listCompanys;

      if (this.selectedType) {
        filtered = filtered.filter((company) => company.typeSystem == this.selectedType);
      }

      if (this.selectedType === '1' && this.selectedTypeBusiness) {
        filtered = filtered.filter(
          (company) => String(company.typeBusiness) === String(this.selectedTypeBusiness),
        );
      }

      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const search = this.searchTerm.toLowerCase().trim();
        filtered = filtered.filter(
          (company) => company.name && company.name.toLowerCase().includes(search),
        );
      }

      if (this.searchCnpj && this.searchCnpj.trim() !== '') {
        const search = this.searchCnpj.replace(/[^\d]/g, '');
        filtered = filtered.filter((company) => {
          if (!company.idFiscal) return false;
          const cnpj = company.idFiscal.replace(/[^\d]/g, '');
          return cnpj.includes(search);
        });
      }

      if (this.searchDate) {
        const searchDateObj = new Date(this.searchDate);
        filtered = filtered.filter((company) => {
          if (!company.dateInsert) return false;
          const companyDate = new Date(company.dateInsert);
          return companyDate.toDateString() === searchDateObj.toDateString();
        });
      }

      return filtered;
    },
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredCompanys.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredCompanys.length / this.pageSize));
    },
    resultsLabel() {
      const count = this.filteredCompanys.length;
      return `${formatNumberBR(count)} ${count === 1 ? 'empresa encontrada' : 'empresas encontradas'}`;
    },
    syncConflicts() {
      const details = this.syncResult?.conflictDetails;
      return Array.isArray(details) ? details.filter(Boolean) : [];
    },
    syncSummary() {
      const created = Number(this.syncResult?.created) || 0;
      const conflicts = Number(this.syncResult?.conflicts) || 0;
      if (created === 0 && conflicts === 0) {
        return 'Nenhuma conta nova. Os super admins ativos já estão vinculados às empresas.';
      }
      const createdLabel = created === 1 ? '1 vínculo criado' : `${created} vínculos criados`;
      const conflictLabel = conflicts === 1 ? '1 conflito' : `${conflicts} conflitos`;
      return `${createdLabel}. ${conflictLabel}.`;
    },
    summaryMetrics() {
      const rows = this.filteredCompanys;
      const weeduc = rows.filter((c) => Number(c.typeSystem) === 1).length;
      const weflix = rows.filter((c) => Number(c.typeSystem) === 2).length;
      const corporativo = rows.filter(
        (c) => Number(c.typeSystem) === 1 && Number(c.typeBusiness) === 2,
      ).length;
      return [
        { label: 'Total', value: formatNumberBR(rows.length) },
        { label: 'Weeduc', value: formatNumberBR(weeduc) },
        { label: 'Weflix', value: formatNumberBR(weflix) },
        { label: 'Corporativo', value: formatNumberBR(corporativo) },
      ];
    },
  },
  methods: {
    formatDateBR,
    formatCnpj(value) {
      if (!value) return '—';
      return String(value).replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
      );
    },
    formatPhone(value) {
      if (!value) return '—';
      return String(value).replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
    },
    async listCompany() {
      try {
        const response = await superAdmin.listCompany(this.token);
        this.listCompanys = response;
        this.hasReady = true;
      } catch (error) {
        console.error(error);
        this.listCompanys = [
          {
            id: 1,
            name: 'Empresa Preview',
            idFiscal: '12345678000199',
            typeSystem: 1,
            typeBusiness: 1,
            phone: '11999999999',
            dateInsert: new Date().toISOString(),
          },
          {
            id: 2,
            name: 'Weflix Preview',
            idFiscal: '98765432000111',
            typeSystem: 2,
            phone: '11988888888',
            dateInsert: new Date().toISOString(),
          },
        ];
        this.hasReady = true;
      }
    },
    filterByType() {
      this.currentPage = 1;
      if (this.selectedType !== '1') {
        this.selectedTypeBusiness = '';
      }
    },
    onSearch() {
      this.currentPage = 1;
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },
    clearFilters() {
      this.searchTerm = '';
      this.searchCnpj = '';
      this.selectedType = '';
      this.selectedTypeBusiness = '';
      this.searchDate = '';
      this.currentPage = 1;
    },
    async syncCompanies() {
      if (this.syncing) return;
      this.syncing = true;
      this.syncError = '';
      try {
        this.syncResult = await superAdmin.syncSuperAdminCompanies(this.token);
        this.syncConfirming = false;
      } catch (error) {
        console.error('Erro ao sincronizar super admins nas empresas:', error);
        this.syncError = 'Não foi possível sincronizar as contas. Tente novamente.';
      } finally {
        this.syncing = false;
      }
    },
  },
  created() {
    this.listCompany();
  },
};
</script>

<style>
@import url('@/css/weflix/super-admin-list.css');
@import url('@/components/Weflix/SuperAdmin/Reports/report-table.css');
</style>
